import React, { FC, useState, useEffect, ChangeEvent, FormEvent } from 'react'
import { Select, ISelectOption } from 'components/forms/Selects'
import { stringifyUrl } from 'query-string'
import { Button } from 'transfix-ui/components/Button'
import { SortArrowToggle } from 'transfix-ui/components/SortArrowToggle'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableHead, TableRow, TableCell, TableBody } from 'transfix-ui/components/Table'
import { useGetParentUsers, GetParentUsersQueryParams } from 'apps/marketplace/apiTypes/parentUsers'
import { SORT_ORDER } from 'apps/rms/types/shipperRates'
import { USER_CARRIER_TYPES } from 'apps/user/types/user'
import { Pagination } from 'components/Pagination'
import { TextInput, TextInputIconType } from 'components/atoms'
import { UserRow } from './UserRow'

interface IUsersListProps {
  defaultCompanyType?: string
  defaultApplicationId?: number
  orgMcNumber?: string | null
  orgDotNumber?: string | null
  orgName?: string | null
}

const UsersList: FC<IUsersListProps> = ({
  defaultApplicationId,
  defaultCompanyType,
  orgDotNumber,
  orgMcNumber,
  orgName,
}) => {
  const [filterParams, setFilterParams] = useState<GetParentUsersQueryParams>({
    pageNumber: 1,
    pageSize: 10,
    sortOrder: 'desc',
    sortField: 'created_at',
    query: '',
    userType: '',
    companyType: defaultCompanyType,
    applicationId: defaultApplicationId,
    companyQuery: '',
  })

  const [query, setQuery] = useState<GetParentUsersQueryParams['query']>('')
  const [companyType, setCompanyType] = useState<GetParentUsersQueryParams['companyType']>(
    defaultCompanyType
  )
  const [companyQuery, setCompanyQuery] = useState<GetParentUsersQueryParams['companyQuery']>('')
  const [userType, setUserType] = useState<GetParentUsersQueryParams['userType']>('')

  const { data, error, loading, refetch } = useGetParentUsers({
    queryParams: filterParams,
    lazy: true,
  })

  const onPageChange = (page: { pageCount: number }) => {
    return setFilterParams(currentState => ({
      ...currentState,
      pageNumber: page.pageCount,
    }))
  }

  const handleUserQueryChange = (e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)

  const handleCompanyQueryChange = (e: ChangeEvent<HTMLInputElement>) =>
    setCompanyQuery(e.target.value)

  enum COMPANY_TYPE {
    shipper = 'shipper',
    carrier = 'carrier',
    admin = 'admin',
  }

  const companyTypeOptions = [
    { value: COMPANY_TYPE.shipper, label: 'Shipper' },
    { value: COMPANY_TYPE.carrier, label: 'Carrier' },
    { value: COMPANY_TYPE.admin, label: 'Admin' },
  ]

  const companyTypeHandler = (selection: ISelectOption<COMPANY_TYPE>) => {
    setCompanyType(selection.value)
  }

  const userTypeOptions = [
    { value: USER_CARRIER_TYPES.ownerOperator, label: 'Owner Operator' },
    { value: USER_CARRIER_TYPES.dispatcher, label: 'Dispatcher' },
    { value: USER_CARRIER_TYPES.driver, label: 'Driver' },
  ]

  const userTypeHandler = (selection: ISelectOption<USER_CARRIER_TYPES>) => {
    return setUserType(selection.value)
  }

  const toggleSortOrder = () => {
    setFilterParams(currentState => ({
      ...currentState,
      pageNumber: 1,
      sortOrder: currentState.sortOrder === 'desc' ? 'asc' : 'desc',
    }))
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setFilterParams(currentState => ({
      ...currentState,
      pageNumber: 1,
      query,
      userType,
      companyType,
      companyQuery,
    }))
  }

  useEffect(() => {
    refetch()
  }, [
    filterParams.query,
    filterParams.userType,
    filterParams.companyQuery,
    filterParams.sortOrder,
    filterParams.pageNumber,
  ])

  const defaultSet =
    (defaultCompanyType && defaultApplicationId) ||
    (data?.currentUserCompanyType && data?.currentUserCompanyId)

  const tableSpinner = () => (
    <TableRow>
      <TableCell colSpan={9} className='tc'>
        <div className='tc'>
          <div className='pa6'>
            <Spinner position='relative' />
          </div>
        </div>
      </TableCell>
    </TableRow>
  )

  const refreshPage = () => global.location.reload()
  const tableError = () => (
    <TableRow>
      <TableCell colSpan={9} className='tc'>
        <div className='tc'>
          <div className='pa6'>
            <div className='mb4'>
              Sorry, there was an issue loading results. Please try refreshing your page.
            </div>
            <button className='btn-text-link' onClick={refreshPage}>
              Refresh your page
            </button>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )
  const tableEmpty = () => (
    <TableRow>
      <TableCell colSpan={9} className='tc'>
        <div className='tc'>
          <div className='pa6'>
            <div className='mb4'>No results found for this search.</div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )

  const userRows = () =>
    data?.parentUsers.map(user => <UserRow user={user} key={user.id} defaultSet={!!defaultSet} />)

  const stickyStyle = { top: '45px', zIndex: 2 }

  return (
    <>
      <div className='flex justify-between'>
        <form onSubmit={handleSubmit} className='mt4 flex w-80'>
          <span className='w-20 mr4'>
            <TextInput
              icon={TextInputIconType.search}
              placeholder='User'
              value={query}
              onChange={handleUserQueryChange}
            />
          </span>
          {!defaultSet && (
            <span className='w-20 mr2'>
              <Select
                options={companyTypeOptions}
                value={companyType}
                onChange={companyTypeHandler}
                placeholder='Company Type'
              />
            </span>
          )}
          {!defaultSet &&
            (companyType === COMPANY_TYPE.shipper || companyType === COMPANY_TYPE.carrier) && (
              <span className='w-20 mr4'>
                <TextInput
                  icon={TextInputIconType.search}
                  placeholder='Company'
                  value={companyQuery}
                  onChange={handleCompanyQueryChange}
                />
              </span>
            )}
          {defaultCompanyType === 'carrier' && (
            <span className='w-20 mr2'>
              <Select
                options={userTypeOptions}
                value={userType}
                onChange={userTypeHandler}
                placeholder='User Type'
              />
            </span>
          )}
          <span className='w-20 mr4'>
            <Button type='submit' color='primary' fullWidth size='small'>
              Search
            </Button>
          </span>
        </form>
        <div>
          <a
            href={stringifyUrl({
              url: `${__ROOT__}/users/new`,
              query: {
                orgType: defaultCompanyType || data?.currentUserCompanyType,
                orgId: defaultApplicationId || data?.currentUserCompanyId,
                orgMcNumber,
                orgDotNumber,
                orgName,
              },
            })}
            className='btn btn-primary'
            target='_blank'
            rel='noopener noreferrer'>
            Add User
          </a>
        </div>
      </div>
      <section className='mt4'>
        <Table stickyHeader stickyHeaderStyle={stickyStyle}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>
                <button className='sort' onClick={toggleSortOrder}>
                  User Since <SortArrowToggle direction={filterParams.sortOrder as SORT_ORDER} />
                </button>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Status</TableCell>
              {!defaultSet && <TableCell>Company</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {error && tableError()}
            {loading && tableSpinner()}
            {!loading && data?.parentUsers?.length === 0 && tableEmpty()}
            {!loading && userRows()}
          </TableBody>
        </Table>
        <div className='tc'>
          <Pagination
            currentPage={filterParams.pageNumber}
            hasNextPage={data?.nextPage}
            onClickHandler={onPageChange}
          />
        </div>
      </section>
    </>
  )
}

export default UsersList
